import { useState } from "react";
import { ExternalLink, Clock, BookOpen, Users, Award, CheckCircle, FileText, X } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const CoursePreview = ({ open, onOpenChange, course }) => {
  const [selectedFormat, setSelectedFormat] = useState(null);

  if (!course) return null;

  const getFormatPrice = (format) => {
    const formatData = course.delivery_formats?.find(f => f.format === format);
    return formatData?.price || 0;
  };

  const getFormatDuration = (format) => {
    const formatData = course.delivery_formats?.find(f => f.format === format);
    return formatData?.duration || "TBD";
  };

  const formatName = (format) => {
    return format?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="sr-only">
          <DialogTitle>Course Preview: {course.title}</DialogTitle>
        </DialogHeader>
        
        {/* Preview Banner */}
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm font-medium flex items-center justify-center gap-2">
          <ExternalLink className="h-4 w-4" />
          ADMIN PREVIEW - This is how customers will see this course
        </div>

        {/* Course Header */}
        <div className="relative">
          <div 
            className="h-48 bg-cover bg-center"
            style={{ 
              backgroundImage: course.image_url 
                ? `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7)), url(${course.image_url})`
                : 'linear-gradient(135deg, #00A3A3 0%, #545A5E 100%)'
            }}
          >
            <div className="absolute inset-0 flex items-end p-6">
              <div>
                <div className="flex gap-2 mb-2">
                  <Badge className="bg-white/20 text-white border-white/30">
                    {course.category}
                  </Badge>
                  <Badge className="bg-white/20 text-white border-white/30">
                    {course.certification_level}
                  </Badge>
                  {course.is_featured && (
                    <Badge className="bg-amber-500 text-white">Featured</Badge>
                  )}
                  {!course.is_active && (
                    <Badge className="bg-red-500 text-white">Inactive</Badge>
                  )}
                </div>
                <h1 className="text-2xl font-bold text-white">{course.title}</h1>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Course Description */}
          <div>
            <h2 className="text-lg font-semibold mb-2">About This Course</h2>
            <p className="text-muted-foreground leading-relaxed">
              {course.description || "No description provided."}
            </p>
          </div>

          {/* Key Topics */}
          {course.key_topics?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-brand" />
                Key Topics
              </h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {course.key_topics.map((topic, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-brand mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{topic}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Learning Outcomes */}
          {course.learning_outcomes?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Award className="h-5 w-5 text-brand" />
                What You'll Learn
              </h2>
              <ul className="space-y-2">
                {course.learning_outcomes.map((outcome, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{outcome}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Prerequisites */}
          {course.prerequisites && (
            <div>
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                <Users className="h-5 w-5 text-brand" />
                Prerequisites
              </h2>
              <p className="text-sm text-muted-foreground">{course.prerequisites}</p>
            </div>
          )}

          {/* Delivery Formats & Pricing */}
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Clock className="h-5 w-5 text-brand" />
              Delivery Options & Pricing
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {course.delivery_formats?.map((format, index) => (
                <div 
                  key={index}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedFormat === format.format 
                      ? 'border-brand bg-brand/5 ring-2 ring-brand' 
                      : 'border-border hover:border-brand/50'
                  }`}
                  onClick={() => setSelectedFormat(format.format)}
                >
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    {formatName(format.format)}
                  </div>
                  <div className="text-2xl font-bold text-brand mb-1">
                    ${format.price}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Duration: {format.duration}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Brochure Link */}
          {course.brochure_url && (
            <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
              <FileText className="h-5 w-5 text-brand" />
              <span className="text-sm">Course brochure available</span>
              <a 
                href={course.brochure_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-brand hover:underline ml-auto"
              >
                View PDF
              </a>
            </div>
          )}

          {/* Preview CTA */}
          <div className="border-t pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">
                  {selectedFormat 
                    ? `Selected: ${formatName(selectedFormat)} - $${getFormatPrice(selectedFormat)}`
                    : 'Select a delivery format above'
                  }
                </p>
              </div>
              <Button 
                className="bg-brand hover:bg-brand-dark text-white"
                disabled={!selectedFormat}
              >
                Enroll Now (Preview Only)
              </Button>
            </div>
          </div>

          {/* Course Metadata */}
          <div className="border-t pt-4 text-xs text-muted-foreground">
            <div className="flex flex-wrap gap-4">
              <span>Course ID: {course.id}</span>
              <span>Status: {course.is_active ? 'Active' : 'Inactive'}</span>
              <span>Featured: {course.is_featured ? 'Yes' : 'No'}</span>
              {course.created_at && (
                <span>Created: {new Date(course.created_at).toLocaleDateString()}</span>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CoursePreview;
